// What a breed card may say, and what it has to stay silent about.
//
// REFOCUS §6 asks a breed card for eggs, hardiness and temperament. Eggs has
// been read from biology-config.json for a while (selectors.ts, breedEggBar).
// Temperament is docility, and docility is not decoration: `sim/escape.ts` and
// `sim/catch.ts` compute P(escape) and P(catch) straight off it, so a card
// showing docility is showing the number the sim actually runs on.
//
// Hardiness is not here, and that is the point. biology-config.json carries
// docility, flightiness, broodiness, waterproof plumage and a difficulty
// weight; not one of those is a 0-100 hardiness, and no source gives a spread
// of hardiness between birds either (`sim/access.ts` draws its hidden hardiness
// as a +/-10% multiplier around 1.0, not a score). So the card shows two bars
// instead of three. A missing bar is a gap someone can close; a number here
// would be a promise the sim does not keep.

import type { Breed, ChickenEntity } from '../core/types';
import { BREEDS } from './config';
import { docilityOf } from './genetics';

/**
 * The party role is a design label, not a measurement, so it has no scale and
 * no bar: a role says which part she plays in a party, and inventing a score
 * to go with it is what this file exists to stop. Exhaustive over `Breed` so a
 * breed added later has to be classified on purpose rather than falling through
 * to a default nobody chose.
 */
const BREED_ROLE: Record<Breed, string> = {
  silkie: 'breed.role.support',
  leghorn: 'breed.role.damage',
  rhode_island_red: 'breed.role.enforcer',
  australorp: 'breed.role.tank',
  buff_orpington: 'breed.role.mother',
  // The three the launch list does not offer. ISA Brown has no research row in
  // clay-farm-science at all, and the game has no numbers to build a card from,
  // so the label says only that she is a breed.
  isa_brown: 'breed.role.unknown',
  brahma: 'breed.role.unknown',
  plymouth_rock: 'breed.role.unknown',
};

/**
 * Breeds whose docility has no research behind it, so the card shows no
 * temperament bar for them rather than a number nothing sourced.
 *
 * Every other breed's docility is a design placement on a BRD row that rates
 * the breed docile or flighty in words, which is as close to a temperament
 * score as the published work gets. ISA Brown is the exception: GIMME-DATA
 * tracks the missing breed row, so there is nothing to place a number on.
 *
 * Kept as a set in code rather than read from biology-config.sources.json so the
 * app does not ship the provenance file, and pinned to that file by a test so it
 * cannot drift: when the ISA Brown research lands, the test fails and the set
 * is corrected rather than the bar quietly staying wrong.
 */
export const TEMPERAMENT_UNSOURCED: readonly Breed[] = ['isa_brown'];

export function breedRole(breed: Breed): string {
  return BREED_ROLE[breed];
}

/** True when the config's docility for this breed rests on real research. */
export function hasSourcedTemperament(breed: Breed): boolean {
  return !TEMPERAMENT_UNSOURCED.includes(breed);
}

/**
 * The breed's docility, 0-100 — the number the buy screen shows, because no
 * bird exists yet. Null when no research stands behind it.
 */
export function breedTemperament(breed: Breed): number | null {
  if (!hasSourcedTemperament(breed)) return null;
  return BREEDS[breed]?.docility ?? null;
}

/**
 * This bird's own docility, which is what the sim runs her escape and catch
 * odds on. A flighty genotype carries the penalty `sim/genetics.ts` applies, so
 * two Silkies can differ, and the card shows the bird in front of the player
 * rather than the breed average.
 */
export function birdTemperament(bird: ChickenEntity): number | null {
  if (!hasSourcedTemperament(bird.breed)) return null;
  return docilityOf(bird.breed, bird.genotype);
}
